import { useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// NeuralCanvasV2 — V4 Edition
//
// Improvements over V3:
//   ✅ [FISICA] Segnali Ricochet — impulsi che viaggiano edge→edge (no flash casuale)
//   ✅ [FISICA] Separazione minima nodi — correzione posizionale MIN_NODE_GAP
//   (tutti i fix V3 mantenuti)
// ─────────────────────────────────────────────────────────────────────────────

const MAX_DIST = 142;
const DAMPING = 0.87;
const BRIDGE_DIST = 340;
const REPEL_R = 72;          // raggio repulsione ampliato
const REPEL_STR = 3500;      // forza repulsione più forte
const MIN_SPEED = 0.08;
const STAR_COUNT = 220;

// ── Ricochet signal system ────────────────────────────────────────────────────
const SIGNAL_SPEED = 0.032;   // 0..1 per frame → ~31 frame per edge
const MAX_SIGNALS = 12;      // concurrent signals max — no saturation
const MAX_SIGNAL_HOPS = 5;       // rimbalzi massimi per catena
const SIGNAL_SPAWN_INTERVAL = 1.4;     // secondi tra auto-spawn di nuove catene
const MIN_NODE_GAP = 18;      // px separazione minima garantita

// ── Formazioni geometriche ────────────────────────────────────────────────────
// Ogni def: { angles: [deg], radii: [0..1] } — angoli e raggi per ogni slot
const FORM_DEFS = [
    // Semplici
    { name: 'line', angles: [0, 180], radii: [1, 1] },
    { name: 'triangle', angles: [270, 30, 150], radii: [1, 1, 1] },
    { name: 'square', angles: [45, 135, 225, 315], radii: [1, 1, 1, 1] },
    { name: 'rhombus', angles: [270, 20, 90, 160], radii: [1.2, 0.9, 1.2, 0.9] },
    // Medie
    { name: 'pentagon', angles: [270, 342, 54, 126, 198], radii: [1, 1, 1, 1, 1] },
    { name: 'hexagon', angles: [0, 60, 120, 180, 240, 300], radii: [1, 1, 1, 1, 1, 1] },
    { name: 'star4', angles: [0, 45, 90, 135, 180, 225, 270, 315], radii: [1, .4, 1, .4, 1, .4, 1, .4] },
    // Complesse
    { name: 'star5', angles: [270, 306, 342, 18, 54, 90, 126, 162, 198, 234], radii: [1, .42, 1, .42, 1, .42, 1, .42, 1, .42] },
    { name: 'octagon', angles: [0, 45, 90, 135, 180, 225, 270, 315], radii: [1, 1, 1, 1, 1, 1, 1, 1] },
    { name: 'hexstar', angles: [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330], radii: [1, .5, 1, .5, 1, .5, 1, .5, 1, .5, 1, .5] },
    // Asimmetriche
    { name: 'arrow', angles: [0, 210, 270, 330, 180], radii: [1.5, 1, 0.5, 1, 0.6] },
    { name: 'cross', angles: [0, 90, 180, 270, 45, 135, 225, 315], radii: [1, 1, 1, 1, .45, .45, .45, .45] },
];
const FORMATION_COUNT = 10;  // formazioni 2D attive
const FORM_STRENGTH = 0.005; // forza morbida — la fisica vince sempre
const FORM_DRIFT_R = 80;   // raggio drift del centroide

// ── Formazioni 3D ────────────────────────────────────────────────────────────
const FOV_DIST = 260;  // distanza focale prospettica
const FORM_3D_DEFS = [
    { name: 'tetrahedron', verts: [[0, 1, 0], [0.943, -0.333, 0], [-0.471, -0.333, 0.816], [-0.471, -0.333, -0.816]] },
    { name: 'cube', verts: [[-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1], [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]] },
    { name: 'octahedron', verts: [[0, 1, 0], [0, -1, 0], [1, 0, 0], [-1, 0, 0], [0, 0, 1], [0, 0, -1]] },
    { name: 'prism', verts: [[0, 1, -0.6], [0.866, -0.5, -0.6], [-0.866, -0.5, -0.6], [0, 1, 0.6], [0.866, -0.5, 0.6], [-0.866, -0.5, 0.6]] },
    { name: 'pyramid', verts: [[0, 1, 0], [1, -0.5, 1], [1, -0.5, -1], [-1, -0.5, -1], [-1, -0.5, 1]] },
    { name: 'bipyramid', verts: [[0, 1, 0], [0, -1, 0], [1, 0, 0], [-1, 0, 0], [0, 0, 1], [0, 0, -1]] },
];
const FORMATION_3D_COUNT = 5;

// Helper: ruota un punto 3D attorno agli assi X, Y, Z
function rotate3D(vx, vy, vz, rx, ry, rz) {
    let y1 = vy * Math.cos(rx) - vz * Math.sin(rx), z1 = vy * Math.sin(rx) + vz * Math.cos(rx);
    let x2 = vx * Math.cos(ry) + z1 * Math.sin(ry), z2 = -vx * Math.sin(ry) + z1 * Math.cos(ry);
    let x3 = x2 * Math.cos(rz) - y1 * Math.sin(rz), y3 = x2 * Math.sin(rz) + y1 * Math.cos(rz);
    return [x3, y3, z2];
}

// ── Color schemes ─────────────────────────────────────────────────────────────
const COLOR_SCHEMES = {
    violet: [
        'rgba(167,139,250,',
        'rgba(196,125,250,',
        'rgba(232,121,249,',
        'rgba(129,140,248,',
        'rgba(167,139,250,',
    ],
    cyan: [
        'rgba(34,211,238,',
        'rgba(6,182,212,',
        'rgba(56,189,248,',
        'rgba(99,240,229,',
        'rgba(34,211,238,',
    ],
    amber: [
        'rgba(251,191,36,',
        'rgba(245,158,11,',
        'rgba(252,211,77,',
        'rgba(234,179,8,',
        'rgba(251,191,36,',
    ],
    emerald: [
        'rgba(52,211,153,',
        'rgba(16,185,129,',
        'rgba(110,231,183,',
        'rgba(6,182,212,',
        'rgba(52,211,153,',
    ],
};

const EDGE_RGB = {
    violet: '139,92,246',
    cyan: '6,182,212',
    amber: '245,158,11',
    emerald: '16,185,129',
};

const NEBULA_COLORS = {
    violet: ['rgba(139,92,246,', 'rgba(99,102,241,', 'rgba(232,121,249,', 'rgba(167,139,250,', 'rgba(99,102,241,', 'rgba(139,92,246,'],
    cyan: ['rgba(6,182,212,', 'rgba(34,211,238,', 'rgba(56,189,248,', 'rgba(6,182,212,', 'rgba(34,211,238,', 'rgba(56,189,248,'],
    amber: ['rgba(245,158,11,', 'rgba(251,191,36,', 'rgba(249,115,22,', 'rgba(245,158,11,', 'rgba(251,191,36,', 'rgba(249,115,22,'],
    emerald: ['rgba(16,185,129,', 'rgba(52,211,153,', 'rgba(6,182,212,', 'rgba(16,185,129,', 'rgba(52,211,153,', 'rgba(6,182,212,'],
};

const HUB_GLOW = { macro: 9, meso: 5, micro: 3 };

// ── 12 hub definitions ────────────────────────────────────────────────────────
const HUB_DEFS = [
    { tier: 'macro', period: 12, baseRadius: 310, radiusVariance: 65, radiusSpeed: 0.28, orbitR: 90, hubStrength: 0.32, driftSpeed: 0.0006, driftR: 120 },
    { tier: 'macro', period: 15, baseRadius: 270, radiusVariance: 80, radiusSpeed: 0.35, orbitR: 75, hubStrength: 0.29, driftSpeed: 0.0008, driftR: 95 },
    { tier: 'macro', period: 11, baseRadius: 340, radiusVariance: 55, radiusSpeed: 0.22, orbitR: 105, hubStrength: 0.34, driftSpeed: 0.0005, driftR: 140 },
    { tier: 'meso', period: 7, baseRadius: 175, radiusVariance: 40, radiusSpeed: 0.45, orbitR: 52, hubStrength: 0.22, driftSpeed: 0.0012, driftR: 80 },
    { tier: 'meso', period: 6, baseRadius: 155, radiusVariance: 35, radiusSpeed: 0.55, orbitR: 44, hubStrength: 0.20, driftSpeed: 0.0014, driftR: 70 },
    { tier: 'meso', period: 8, baseRadius: 190, radiusVariance: 45, radiusSpeed: 0.38, orbitR: 58, hubStrength: 0.23, driftSpeed: 0.0010, driftR: 90 },
    { tier: 'meso', period: 5, baseRadius: 145, radiusVariance: 30, radiusSpeed: 0.60, orbitR: 40, hubStrength: 0.20, driftSpeed: 0.0016, driftR: 62 },
    { tier: 'micro', period: 3, baseRadius: 82, radiusVariance: 22, radiusSpeed: 0.80, orbitR: 24, hubStrength: 0.18, driftSpeed: 0.0024, driftR: 48 },
    { tier: 'micro', period: 2.5, baseRadius: 68, radiusVariance: 18, radiusSpeed: 0.90, orbitR: 19, hubStrength: 0.17, driftSpeed: 0.0027, driftR: 38 },
    { tier: 'micro', period: 3.5, baseRadius: 88, radiusVariance: 25, radiusSpeed: 0.72, orbitR: 27, hubStrength: 0.18, driftSpeed: 0.0021, driftR: 52 },
    { tier: 'micro', period: 2, baseRadius: 60, radiusVariance: 15, radiusSpeed: 1.00, orbitR: 17, hubStrength: 0.16, driftSpeed: 0.0030, driftR: 33 },
    { tier: 'micro', period: 4, baseRadius: 94, radiusVariance: 28, radiusSpeed: 0.65, orbitR: 30, hubStrength: 0.18, driftSpeed: 0.0019, driftR: 58 },
];

// ── Spatial grid helpers ──────────────────────────────────────────────────────
function buildGrid(nodes, cellSize, W) {
    const gCols = Math.ceil(W / cellSize) || 1;
    const grid = new Map();
    nodes.forEach((n, idx) => {
        const key = Math.floor(n.y / cellSize) * gCols + Math.floor(n.x / cellSize);
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key).push(idx);
    });
    return { grid, gCols };
}

function getNeighbors(grid, gCols, n, cellSize) {
    const cx = Math.floor(n.x / cellSize);
    const cy = Math.floor(n.y / cellSize);
    const result = [];
    for (let dcx = -1; dcx <= 1; dcx++) {
        for (let dcy = -1; dcy <= 1; dcy++) {
            const nb = grid.get((cy + dcy) * gCols + (cx + dcx));
            if (nb) nb.forEach(idx => result.push(idx));
        }
    }
    return result;
}

// ── Ricochet signal helper ────────────────────────────────────────────────────
/**
 * Prova a creare un segnale che parte da fromIdx verso un vicino non visitato.
 * @param {Array}  nodes
 * @param {number} fromIdx
 * @param {Map}    grid
 * @param {number} gCols
 * @param {Set}    [visitedSet]  nodi già attraversati nella catena
 * @param {number} [hops]
 * @returns {object|null}
 */
function spawnSignal(nodes, fromIdx, grid, gCols, visitedSet = new Set(), hops = 0) {
    const from = nodes[fromIdx];
    if (!from) return null;
    const candidates = getNeighbors(grid, gCols, from, MAX_DIST)
        .filter(j => j !== fromIdx && !visitedSet.has(j) &&
            Math.hypot(nodes[j].x - from.x, nodes[j].y - from.y) < MAX_DIST);
    if (candidates.length === 0) return null;
    const toIdx = candidates[Math.floor(Math.random() * candidates.length)];
    const nextVisited = new Set(visitedSet);
    nextVisited.add(fromIdx);
    return { fromIdx, toIdx, progress: 0, hops, colorBase: from.colorBase, visitedSet: nextVisited };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * NeuralCanvasV2 — animated neural network background (V4)
 *
 * @prop {number}  nodeCount      - Override node count (default: auto)
 * @prop {'violet'|'cyan'|'amber'|'emerald'} colorScheme
 * @prop {boolean} interactive    - Mouse/touch interaction (default: true)
 * @prop {boolean} reducedMotion  - Reduce animation intensity (default: false)
 */
const NeuralCanvasV2 = ({
    nodeCount,
    colorScheme = 'violet',
    interactive = true,
    reducedMotion = false,
}) => {
    const canvasRef = useRef(null);
    const animRef = useRef(null);
    const mouseRef = useRef({ x: -9999, y: -9999 });
    const holdRef = useRef({ isDown: false, x: 0, y: 0, startMs: 0, charge: 0, releasing: false, releaseLife: 0 });
    const nodesRef = useRef([]);
    const hubsRef = useRef([]);
    const signalsRef = useRef([]);      // ricochet signals
    const lastSignalSpawnRef = useRef(0);
    const bridgesRef = useRef([]);
    const lastBridgeRef = useRef(0);
    const dprRef = useRef(1);
    const sizeRef = useRef({ W: 0, H: 0 });
    const starsRef = useRef([]);
    const formationsRef = useRef([]);   // geometric formation groups

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const updateDPR = () => { dprRef.current = window.devicePixelRatio || 1; };
        updateDPR();

        const NODE_COLORS = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.violet;
        const NEBULA = NEBULA_COLORS[colorScheme] || NEBULA_COLORS.violet;
        const edgeRgb = EDGE_RGB[colorScheme] || EDGE_RGB.violet;
        const cursorColor0 = NODE_COLORS[0];
        const cursorColor2 = NODE_COLORS[2];

        const getNodeCount = () => {
            if (nodeCount) return nodeCount;
            const isMobile = window.innerWidth < 768;
            const isLowEnd = navigator.hardwareConcurrency <= 4;
            if (reducedMotion) return 60;
            if (isMobile) return isLowEnd ? 70 : 90;
            return isLowEnd ? 110 : 140;
        };

        const resize = () => {
            const dpr = dprRef.current;
            const W = window.innerWidth;
            const H = window.innerHeight;
            sizeRef.current = { W, H };
            canvas.style.width = W + 'px';
            canvas.style.height = H + 'px';
            canvas.width = Math.round(W * dpr);
            canvas.height = Math.round(H * dpr);
            ctx.scale(dpr, dpr);
            const hubs = hubsRef.current;
            if (hubs.length > 0) {
                const oldW = hubs[0]._lastW || W;
                const oldH = hubs[0]._lastH || H;
                hubs.forEach(h => {
                    h.anchorX = (h.anchorX / oldW) * W;
                    h.anchorY = (h.anchorY / oldH) * H;
                    h._lastW = W;
                    h._lastH = H;
                });
            }
        };
        resize();
        window.addEventListener('resize', resize);

        const { W: W0, H: H0 } = sizeRef.current;
        const N = getNodeCount();

        // ── Init nodes ────────────────────────────────────────────────────────
        const allNodes = Array.from({ length: N }, () => ({
            x: Math.random() * W0,
            y: Math.random() * H0,
            vx: (Math.random() - 0.5) * 0.7,
            vy: (Math.random() - 0.5) * 0.7,
            r: 0.7 + Math.random() * 1.8,
            colorBase: NODE_COLORS[Math.floor(Math.random() * NODE_COLORS.length)],
            pulse: Math.random() * Math.PI * 2,
            flashLife: 0,
            zDepth: 0,    // -1..1, aggiornato dalle formazioni 3D
            wanderAngle: Math.random() * Math.PI * 2,
            wanderSpeed: 0.002 + Math.random() * 0.004,
            wanderForce: 0.0015 + Math.random() * 0.002,
            isHub: false,
        }));

        // ── Init hubs ─────────────────────────────────────────────────────────
        const gridCols = 4, gridRows = 3;
        const hubs = HUB_DEFS.map((def, i) => {
            const col = i % gridCols, row = Math.floor(i / gridCols);
            const sx = (col + 0.5 + (Math.random() - 0.5) * 0.6) / gridCols * W0;
            const sy = (row + 0.5 + (Math.random() - 0.5) * 0.6) / gridRows * H0;
            const n = allNodes[i];
            n.x = sx; n.y = sy; n.isHub = true;
            return {
                node: n, def,
                anchorX: sx, anchorY: sy,
                _lastW: W0, _lastH: H0,
                orbitAngle: Math.random() * Math.PI * 2,
                orbitA: def.driftR * (0.7 + Math.random() * 0.6),
                orbitB: def.driftR * (0.5 + Math.random() * 0.5),
                orbitTilt: Math.random() * Math.PI,
                hubPhase: Math.random() * Math.PI * 2,
                influence: 0,
                hubRadius: 0,
            };
        });
        hubsRef.current = hubs;
        nodesRef.current = allNodes;

        // ── Init formazioni geometriche ───────────────────────────────────────
        // Nodi non-hub disponibili per le formazioni
        const nonHubIndices = allNodes.map((n, i) => i).filter(i => !allNodes[i].isHub);
        // Shuffle
        for (let i = nonHubIndices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [nonHubIndices[i], nonHubIndices[j]] = [nonHubIndices[j], nonHubIndices[i]];
        }
        let nodePool = [...nonHubIndices];
        const formations = [];
        for (let fi = 0; fi < FORMATION_COUNT && nodePool.length >= 2; fi++) {
            const def = FORM_DEFS[fi % FORM_DEFS.length];
            const slots = def.angles.length;
            if (nodePool.length < slots) break;
            const nodeIndices = nodePool.splice(0, slots);
            const anchorX = Math.random() * W0;
            const anchorY = Math.random() * H0;
            // Scala il raggio in base alla complessità della forma
            const baseR = 30 + (slots - 2) * 7 + Math.random() * 25;
            formations.push({
                def,
                nodeIndices,
                anchorX, anchorY,
                cx: anchorX, cy: anchorY,
                driftAngle: Math.random() * Math.PI * 2,
                driftSpeed: 0.0004 + Math.random() * 0.0004,
                driftR: FORM_DRIFT_R * (0.5 + Math.random() * 0.8),
                driftPhaseY: Math.random() * Math.PI * 2,
                rot: Math.random() * 360,
                rotSpeed: (Math.random() - 0.5) * 0.04, // gradi/frame (lenta)
                radius: baseR,
                strength: FORM_STRENGTH * (0.7 + Math.random() * 0.6),
            });
        }

        // ── Init formazioni 3D ────────────────────────────────────────────────
        const formations3D = [];
        for (let fi = 0; fi < FORMATION_3D_COUNT && nodePool.length >= 4; fi++) {
            const def = FORM_3D_DEFS[fi % FORM_3D_DEFS.length];
            const slots = def.verts.length;
            if (nodePool.length < slots) break;
            const nodeIndices = nodePool.splice(0, slots);
            const baseR = 38 + Math.random() * 30;
            formations3D.push({
                def, nodeIndices, is3D: true,
                anchorX: 80 + Math.random() * (W0 - 160),
                anchorY: 80 + Math.random() * (H0 - 160),
                cx: 0, cy: 0,
                driftAngle: Math.random() * Math.PI * 2,
                driftSpeed: 0.0003 + Math.random() * 0.0003,
                driftR: 60 + Math.random() * 60,
                driftPhaseY: Math.random() * Math.PI * 2,
                rx: Math.random() * Math.PI * 2,
                ry: Math.random() * Math.PI * 2,
                rz: Math.random() * Math.PI * 2,
                rxSpd: (Math.random() - 0.5) * 0.006,
                rySpd: (Math.random() - 0.5) * 0.007,
                rzSpd: (Math.random() - 0.5) * 0.005,
                radius: baseR,
                strength: 0.006,
            });
        }
        formationsRef.current = [...formations, ...formations3D];

        // ── Warm-up: pre-simula le forze per partire già in formazione ────────
        // Evita che i nodi "corrano" visibilmente verso la posizione al caricamento
        const allFm = formationsRef.current;
        const WARMUP_ITERS = 280;
        const WARMUP_STR = FORM_STRENGTH * 10;   // forza 10× per convergenza rapida
        const WARMUP_DAMP = 0.78;                 // damping aggressivo
        const WARMUP_GAP = MIN_NODE_GAP * 0.85;

        for (let iter = 0; iter < WARMUP_ITERS; iter++) {
            // Applica forze formazione
            allFm.forEach(fm => {
                // Centroide fisso durante warm-up (usa anchorX/anchorY)
                const cx0 = fm.anchorX, cy0 = fm.anchorY;
                if (fm.is3D) {
                    fm.nodeIndices.forEach((ni, k) => {
                        const n = allNodes[ni]; if (!n || n.isHub) return;
                        const [vx, vy, vz] = fm.def.verts[k];
                        const [rx3, ry3, rz3] = rotate3D(vx, vy, vz, fm.rx, fm.ry, fm.rz);
                        const scale = FOV_DIST / (FOV_DIST + rz3 * fm.radius * 0.5);
                        const tx = cx0 + rx3 * fm.radius * scale;
                        const ty = cy0 + ry3 * fm.radius * scale;
                        const dx = tx - n.x, dy = ty - n.y;
                        const d = Math.hypot(dx, dy);
                        if (d > 0.5) { const f = Math.min(d, 12) * WARMUP_STR; n.vx += (dx / d) * f; n.vy += (dy / d) * f; }
                    });
                } else {
                    const rotRad = fm.rot * Math.PI / 180;
                    fm.nodeIndices.forEach((ni, k) => {
                        const n = allNodes[ni]; if (!n || n.isHub) return;
                        const a = fm.def.angles[k] * Math.PI / 180 + rotRad;
                        const rM = fm.def.radii ? fm.def.radii[k] : 1;
                        const tx = cx0 + Math.cos(a) * fm.radius * rM;
                        const ty = cy0 + Math.sin(a) * fm.radius * rM;
                        const dx = tx - n.x, dy = ty - n.y;
                        const d = Math.hypot(dx, dy);
                        if (d > 0.5) { const f = Math.min(d, 12) * WARMUP_STR; n.vx += (dx / d) * f; n.vy += (dy / d) * f; }
                    });
                }
            });
            // Damping + integra posizione
            allNodes.forEach(n => {
                if (n.isHub) return;
                n.vx *= WARMUP_DAMP; n.vy *= WARMUP_DAMP;
                n.x += n.vx; n.y += n.vy;
                if (n.x < 0) n.x += W0; else if (n.x >= W0) n.x -= W0;
                if (n.y < 0) n.y += H0; else if (n.y >= H0) n.y -= H0;
            });
            // Correzione separazione leggera ogni 5 iterazioni
            if (iter % 5 === 0) {
                allNodes.forEach((a, i) => {
                    if (a.isHub) return;
                    allNodes.forEach((b, j) => {
                        if (j <= i || b.isHub) return;
                        const dx = b.x - a.x, dy = b.y - a.y;
                        const d = Math.hypot(dx, dy);
                        if (d < WARMUP_GAP && d > 0.001) {
                            const push = (WARMUP_GAP - d) * 0.4;
                            const nx = dx / d, ny = dy / d;
                            a.x -= nx * push; a.y -= ny * push;
                            b.x += nx * push; b.y += ny * push;
                        }
                    });
                });
            }
        }
        // Azzera velocità post-warmup per evitare overshoot al primo frame
        allNodes.forEach(n => { n.vx = 0; n.vy = 0; });

        // ── Init stelle ───────────────────────────────────────────────────────
        starsRef.current = Array.from({ length: STAR_COUNT }, () => ({
            x: Math.random(), y: Math.random(),
            r: 0.3 + Math.random() * 0.85,
            a: 0.10 + Math.random() * 0.35,
            twinkle: Math.random() * Math.PI * 2,
            twinkleSpeed: 0.004 + Math.random() * 0.008,
        }));

        // ── 6 Nebula blobs ────────────────────────────────────────────────────
        const blobs = [
            { cx: 0.15, cy: 0.20, color: NEBULA[0], r: 0.30, phase: 0.0, driftX: 0.018, driftY: 0.011 },
            { cx: 0.80, cy: 0.30, color: NEBULA[1], r: 0.26, phase: 2.1, driftX: 0.014, driftY: 0.019 },
            { cx: 0.50, cy: 0.80, color: NEBULA[2], r: 0.24, phase: 4.2, driftX: 0.021, driftY: 0.013 },
            { cx: 0.25, cy: 0.65, color: NEBULA[3], r: 0.16, phase: 1.5, driftX: 0.016, driftY: 0.022 },
            { cx: 0.65, cy: 0.15, color: NEBULA[4], r: 0.20, phase: 3.3, driftX: 0.012, driftY: 0.017 },
            { cx: 0.40, cy: 0.50, color: NEBULA[5], r: 0.13, phase: 5.1, driftX: 0.025, driftY: 0.010 },
        ];

        // ── Event listeners ───────────────────────────────────────────────────
        const getXY = (clientX, clientY) => ({ x: clientX, y: clientY });
        const onMouseMove = e => { if (!interactive) return; mouseRef.current = getXY(e.clientX, e.clientY); };
        const onMouseLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };
        const onMouseDown = e => {
            if (!interactive || e.button !== 0) return;
            holdRef.current = { isDown: true, x: e.clientX, y: e.clientY, startMs: performance.now(), charge: 0, releasing: false, releaseLife: 0 };
        };
        const onMouseUp = () => {
            const h = holdRef.current;
            if (!h.isDown) return;
            h.isDown = false; h.releasing = true; h.releaseLife = 1.0;
        };
        const onTouchStart = e => {
            const touch = e.touches[0];
            mouseRef.current = getXY(touch.clientX, touch.clientY);
            if (!interactive) return;
            holdRef.current = { isDown: true, x: touch.clientX, y: touch.clientY, startMs: performance.now(), charge: 0, releasing: false, releaseLife: 0 };
        };
        const onTouchMove = e => {
            if (!interactive) return;
            e.preventDefault();
            const t = e.touches[0];
            mouseRef.current = getXY(t.clientX, t.clientY);
        };
        const onTouchEnd = () => {
            mouseRef.current = { x: -9999, y: -9999 };
            const h = holdRef.current;
            if (!h.isDown) return;
            h.isDown = false; h.releasing = true; h.releaseLife = 1.0;
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseleave', onMouseLeave);
        window.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mouseup', onMouseUp);
        canvas.addEventListener('touchmove', onTouchMove, { passive: false });
        canvas.addEventListener('touchstart', onTouchStart, { passive: true });
        canvas.addEventListener('touchend', onTouchEnd, { passive: true });

        // ── Cursor renderer ───────────────────────────────────────────────────
        const drawCursor = (nodes, mouse) => {
            if (mouse.x < 0) return;
            let nearest = null, nd = 150;
            nodes.forEach(n => {
                const d = Math.hypot(n.x - mouse.x, n.y - mouse.y);
                if (d < nd) { nd = d; nearest = n; }
            });
            if (!nearest) return;
            const g = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 14);
            g.addColorStop(0, `${cursorColor0}0.30)`);
            g.addColorStop(1, 'transparent');
            ctx.beginPath(); ctx.arc(mouse.x, mouse.y, 14, 0, Math.PI * 2);
            ctx.fillStyle = g; ctx.fill();
            ctx.beginPath(); ctx.moveTo(mouse.x, mouse.y); ctx.lineTo(nearest.x, nearest.y);
            ctx.strokeStyle = `${cursorColor2}0.6)`; ctx.lineWidth = 1.0; ctx.stroke();
            const hop1 = [];
            nodes.forEach(n => {
                if (n === nearest || Math.hypot(n.x - nearest.x, n.y - nearest.y) >= MAX_DIST) return;
                hop1.push(n);
                ctx.beginPath(); ctx.moveTo(nearest.x, nearest.y); ctx.lineTo(n.x, n.y);
                ctx.strokeStyle = `${cursorColor0}0.30)`; ctx.lineWidth = 0.7; ctx.stroke();
            });
            hop1.forEach(h1 => nodes.forEach(n => {
                if (n === nearest || hop1.includes(n)) return;
                if (Math.hypot(n.x - h1.x, n.y - h1.y) < MAX_DIST) {
                    ctx.beginPath(); ctx.moveTo(h1.x, h1.y); ctx.lineTo(n.x, n.y);
                    ctx.strokeStyle = `${cursorColor0}0.10)`; ctx.lineWidth = 0.5; ctx.stroke();
                }
            }));
        };

        // ── Main draw loop ────────────────────────────────────────────────────
        const draw = () => {
            const { W, H } = sizeRef.current;
            const nodes = nodesRef.current;
            const hubs = hubsRef.current;
            const mouse = mouseRef.current;
            const hold = holdRef.current;
            const t = performance.now() * 0.001;

            if (hold.isDown) {
                hold.charge = Math.min(1.0, (performance.now() - hold.startMs) / 2000);
                if (mouse.x > 0) { hold.x = mouse.x; hold.y = mouse.y; }
            }

            ctx.setTransform(dprRef.current, 0, 0, dprRef.current, 0, 0);
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = '#030014'; ctx.fillRect(0, 0, W, H);

            // ── Stelle di sfondo ──────────────────────────────────────────────
            starsRef.current.forEach(s => {
                s.twinkle += s.twinkleSpeed;
                const alpha = s.a * (0.6 + 0.4 * Math.sin(s.twinkle));
                ctx.beginPath();
                ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
                ctx.fill();
            });

            // ── Nebula blobs ──────────────────────────────────────────────────
            blobs.forEach(b => {
                b.phase += reducedMotion ? 0.001 : 0.004;
                const al = 0.085 + Math.sin(b.phase) * 0.035;
                const cx = b.cx * W + Math.sin(b.phase * b.driftX * 40) * W * 0.05;
                const cy = b.cy * H + Math.cos(b.phase * b.driftY * 30) * H * 0.04;
                const r = b.r * Math.min(W, H) * (1 + Math.sin(b.phase * 1.2) * 0.08);
                const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
                grd.addColorStop(0, `${b.color}${Math.max(0, al).toFixed(3)})`);
                grd.addColorStop(1, 'transparent');
                ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.fillStyle = grd; ctx.fill();
            });

            // ── Aurora overlay ────────────────────────────────────────────────
            if (!reducedMotion) {
                const auroraY = H * (0.3 + Math.sin(t * 0.18) * 0.12);
                const auroraGrad = ctx.createLinearGradient(0, auroraY - H * 0.3, 0, auroraY + H * 0.3);
                auroraGrad.addColorStop(0, 'transparent');
                auroraGrad.addColorStop(0.5, `${NODE_COLORS[0]}${(0.04 + Math.sin(t * 0.25) * 0.015).toFixed(3)})`);
                auroraGrad.addColorStop(1, 'transparent');
                ctx.fillStyle = auroraGrad;
                ctx.fillRect(0, 0, W, H);
            }

            // ── 1. Update hubs ────────────────────────────────────────────────
            hubs.forEach(h => {
                const def = h.def;
                h.orbitAngle += def.driftSpeed * (reducedMotion ? 0.4 : 1);
                const ca = Math.cos(h.orbitTilt), sa = Math.sin(h.orbitTilt);
                const lx = Math.cos(h.orbitAngle) * h.orbitA;
                const ly = Math.sin(h.orbitAngle) * h.orbitB;
                let nx = h.anchorX + lx * ca - ly * sa;
                let ny = h.anchorY + lx * sa + ly * ca;
                if (def.tier === 'macro') {
                    const pad = 30;
                    if (nx < pad) nx = pad + (pad - nx);
                    if (nx > W - pad) nx = W - pad - (nx - (W - pad));
                    if (ny < pad) ny = pad + (pad - ny);
                    if (ny > H - pad) ny = H - pad - (ny - (H - pad));
                } else {
                    if (nx < 0) nx += W; else if (nx >= W) nx -= W;
                    if (ny < 0) ny += H; else if (ny >= H) ny -= H;
                }
                h.node.x = nx; h.node.y = ny;
                h.hubPhase += (2 * Math.PI) / (def.period * 60);
                h.influence = Math.sin(h.hubPhase);
                h.hubRadius = def.baseRadius + Math.sin(t * def.radiusSpeed) * def.radiusVariance;
            });

            // ── 2. Hub orbital forces + tangential push ───────────────────────
            const hubMembers = hubs.map(() => []);
            hubs.forEach((h, hi) => {
                const orbit = h.def.orbitR * (1 + Math.sin(t * h.def.radiusSpeed * 0.5) * 0.12);
                nodes.forEach(n => {
                    if (n.isHub) return;
                    const dx = h.node.x - n.x, dy = h.node.y - n.y;
                    const d = Math.hypot(dx, dy);
                    if (d < h.hubRadius && d > 0.5) {
                        const inOrbit = d < orbit;
                        const distEdge = inOrbit
                            ? (orbit - d) / Math.max(orbit, 1)
                            : (d - orbit) / Math.max(h.hubRadius - orbit, 1);
                        const sign = inOrbit ? -1 : 1;
                        const snapBonus = 1 + 0.8 * Math.max(0, 1 - distEdge * 2.8);
                        const f = h.influence * h.def.hubStrength * distEdge * sign * snapBonus * 0.9;
                        n.vx += (dx / d) * f;
                        n.vy += (dy / d) * f;
                        if (h.influence > 0.1) hubMembers[hi].push(n);
                    }
                });
            });

            hubs.forEach((h, hi) => {
                const mlist = hubMembers[hi];
                if (mlist.length < 2) return;
                const minAngDist = (2 * Math.PI / mlist.length) * 0.65;
                const sample = mlist.length > 40 ? mlist.slice(0, 40) : mlist;
                for (let i = 0; i < sample.length; i++) {
                    for (let j = i + 1; j < sample.length; j++) {
                        const a = sample[i], b = sample[j];
                        const angA = Math.atan2(a.y - h.node.y, a.x - h.node.x);
                        const angB = Math.atan2(b.y - h.node.y, b.x - h.node.x);
                        let dAng = angB - angA;
                        if (dAng > Math.PI) dAng -= 2 * Math.PI;
                        if (dAng < -Math.PI) dAng += 2 * Math.PI;
                        const absAng = Math.abs(dAng);
                        if (absAng < minAngDist && absAng > 0.001) {
                            const pushF = (minAngDist - absAng) / minAngDist * 0.055;
                            const dir = Math.sign(dAng);
                            a.vx += -Math.sin(angA) * dir * pushF;
                            a.vy += Math.cos(angA) * dir * pushF;
                            b.vx += Math.sin(angB) * dir * pushF;
                            b.vy += -Math.cos(angB) * dir * pushF;
                        }
                    }
                }
            });

            hubs.forEach((ha, i) => {
                hubs.forEach((hb, j) => {
                    if (j <= i) return;
                    const dx = hb.node.x - ha.node.x, dy = hb.node.y - ha.node.y;
                    const d = Math.hypot(dx, dy);
                    if (d > BRIDGE_DIST * 1.2 || d < 1) return;
                    const bothAttracting = ha.influence > 0 && hb.influence > 0;
                    const oneRepelling = (ha.influence < 0) !== (hb.influence < 0);
                    const fStr = bothAttracting ? 0.006 : oneRepelling ? -0.010 : 0;
                    if (fStr !== 0) {
                        const nx = dx / d, ny = dy / d;
                        ha.node.vx += nx * fStr; ha.node.vy += ny * fStr;
                        hb.node.vx -= nx * fStr; hb.node.vy -= ny * fStr;
                    }
                });
            });

            // ── 3. Wander ─────────────────────────────────────────────────────
            nodes.forEach(n => {
                if (n.isHub) return;
                n.wanderAngle += n.wanderSpeed;
                n.vx += Math.cos(n.wanderAngle) * n.wanderForce;
                n.vy += Math.sin(n.wanderAngle) * n.wanderForce;
            });

            // ── 3b. Forze formazioni geometriche ─────────────────────────────
            formationsRef.current.forEach(fm => {
                const spd = reducedMotion ? 0.3 : 1;
                // Aggiorna centroide con drift lemniscata-like
                fm.driftAngle += fm.driftSpeed * spd;
                fm.cx = fm.anchorX + Math.cos(fm.driftAngle) * fm.driftR;
                fm.cy = fm.anchorY + Math.sin(fm.driftPhaseY + fm.driftAngle * 0.7) * fm.driftR * 0.6;
                // Border clamp — la formazione non esce fuori dallo schermo
                fm.cx = Math.max(80, Math.min(W - 80, fm.cx));
                fm.cy = Math.max(80, Math.min(H - 80, fm.cy));

                if (fm.is3D) {
                    // ── Formazione 3D: ruota e proietta con prospettiva ──────
                    fm.rx += fm.rxSpd * spd; fm.ry += fm.rySpd * spd; fm.rz += fm.rzSpd * spd;
                    fm.nodeIndices.forEach((ni, k) => {
                        const n = nodes[ni];
                        if (!n || n.isHub) return;
                        const [vx, vy, vz] = fm.def.verts[k];
                        const [rx3, ry3, rz3] = rotate3D(vx, vy, vz, fm.rx, fm.ry, fm.rz);
                        // Proiezione prospettica
                        const scale = FOV_DIST / (FOV_DIST + rz3 * fm.radius * 0.5);
                        const tx = fm.cx + rx3 * fm.radius * scale;
                        const ty = fm.cy + ry3 * fm.radius * scale;
                        // Memorizza profondità normalizzata per il rendering
                        n.zDepth = rz3; // -1..1 circa
                        const dx = tx - n.x, dy = ty - n.y;
                        const dist = Math.hypot(dx, dy);
                        if (dist > 0.5) {
                            const forceMag = fm.strength * Math.min(dist / 50, 1.2);
                            n.vx += (dx / dist) * forceMag * dist;
                            n.vy += (dy / dist) * forceMag * dist;
                        }
                    });
                } else {
                    // ── Formazione 2D ─────────────────────────────────────────
                    // Rotazione lenta
                    fm.rot += fm.rotSpeed * spd;
                    const rotRad = fm.rot * Math.PI / 180;
                    // Applica forza morbida verso posizione ideale
                    fm.nodeIndices.forEach((ni, k) => {
                        const n = nodes[ni];
                        if (!n || n.isHub) return;
                        n.zDepth = 0; // Reset zDepth per nodi 2D
                        const angleDef = fm.def.angles[k] * Math.PI / 180;
                        const rMult = fm.def.radii ? fm.def.radii[k] : 1;
                        const a = angleDef + rotRad;
                        const tx = fm.cx + Math.cos(a) * fm.radius * rMult;
                        const ty = fm.cy + Math.sin(a) * fm.radius * rMult;
                        const dx = tx - n.x, dy = ty - n.y;
                        const dist = Math.hypot(dx, dy);
                        if (dist > 0.5) {
                            // Forza inversamente proporzionale alla distanza — vicini tirano meno
                            const forceMag = fm.strength * Math.min(dist / 60, 1.0);
                            n.vx += (dx / dist) * forceMag * dist;
                            n.vy += (dy / dist) * forceMag * dist;
                        }
                    });
                }
            });

            // ── Build unico spatial grid (riutilizzato per repulsion + edge + segnali)
            const { grid: mainGrid, gCols: mainCols } = buildGrid(nodes, MAX_DIST, W);

            // ── 4. Short-range repulsion ──────────────────────────────────────
            nodes.forEach((a, i) => {
                if (a.isHub) return;
                getNeighbors(mainGrid, mainCols, a, REPEL_R).forEach(j => {
                    if (j <= i) return;
                    const b = nodes[j];
                    if (b.isHub) return;
                    const dx = b.x - a.x, dy = b.y - a.y;
                    const d = Math.hypot(dx, dy) || 0.1;
                    if (d < REPEL_R) {
                        const f = REPEL_STR / (d * d * d);
                        const nx = dx / d, ny = dy / d, cap = 0.85;
                        a.vx -= Math.max(-cap, Math.min(cap, nx * f));
                        a.vy -= Math.max(-cap, Math.min(cap, ny * f));
                        b.vx += Math.max(-cap, Math.min(cap, nx * f));
                        b.vy += Math.max(-cap, Math.min(cap, ny * f));
                    }
                });
            });

            // ── 5. Hold-to-attract / release-to-repel ─────────────────────────
            if (interactive) {
                if (hold.isDown && hold.charge > 0 && hold.x > 0) {
                    const ATTRACT_R = 250 + hold.charge * 200;
                    const TARGET_D = 55 + hold.charge * 40;
                    const HOLD_F = 0.045 + hold.charge * 0.10;
                    const attracted = [];
                    nodes.forEach(n => {
                        if (n.isHub) return;
                        const dx = hold.x - n.x, dy = hold.y - n.y;
                        const d = Math.hypot(dx, dy);
                        if (d < ATTRACT_R && d > 0.5) {
                            const delta = d - TARGET_D;
                            const sign = delta > 0 ? 1 : -0.35;
                            const f = HOLD_F * Math.abs(delta) / Math.max(ATTRACT_R, 1) * sign;
                            n.vx += (dx / d) * f;
                            n.vy += (dy / d) * f;
                            if (hold.charge > 0.3)
                                n.flashLife = Math.max(n.flashLife, hold.charge * 0.35);
                            attracted.push(n);
                        }
                    });
                    if (attracted.length >= 2) {
                        const minAngDist = (2 * Math.PI / attracted.length) * 0.70;
                        const sample = attracted.length > 36 ? attracted.slice(0, 36) : attracted;
                        for (let i = 0; i < sample.length; i++) {
                            for (let j = i + 1; j < sample.length; j++) {
                                const a = sample[i], b = sample[j];
                                const angA = Math.atan2(a.y - hold.y, a.x - hold.x);
                                const angB = Math.atan2(b.y - hold.y, b.x - hold.x);
                                let dAng = angB - angA;
                                if (dAng > Math.PI) dAng -= 2 * Math.PI;
                                if (dAng < -Math.PI) dAng += 2 * Math.PI;
                                const absAng = Math.abs(dAng);
                                if (absAng < minAngDist && absAng > 0.001) {
                                    const pushF = (minAngDist - absAng) / minAngDist * 0.08;
                                    const dir = Math.sign(dAng);
                                    a.vx += -Math.sin(angA) * dir * pushF;
                                    a.vy += Math.cos(angA) * dir * pushF;
                                    b.vx += Math.sin(angB) * dir * pushF;
                                    b.vy += -Math.cos(angB) * dir * pushF;
                                }
                            }
                        }
                    }
                    const pulse = Math.sin(t * 8) * 0.5 + 0.5;
                    const ringR1 = TARGET_D * 1.1;
                    const ringR2 = TARGET_D * 1.75;
                    const cA = 0.18 + hold.charge * 0.52;
                    ctx.beginPath(); ctx.arc(hold.x, hold.y, ringR1, 0, Math.PI * 2);
                    ctx.strokeStyle = `${cursorColor2}${(cA * (0.55 + pulse * 0.45)).toFixed(3)})`;
                    ctx.lineWidth = 1.0 + hold.charge * 1.5; ctx.stroke();
                    ctx.beginPath(); ctx.arc(hold.x, hold.y, ringR2, 0, Math.PI * 2);
                    ctx.strokeStyle = `${cursorColor0}${(cA * 0.40).toFixed(3)})`;
                    ctx.lineWidth = 0.6; ctx.stroke();
                    const gCore = ctx.createRadialGradient(hold.x, hold.y, 0, hold.x, hold.y, 14 + hold.charge * 12);
                    gCore.addColorStop(0, `rgba(255,200,255,${(0.18 + hold.charge * 0.55).toFixed(2)})`);
                    gCore.addColorStop(1, 'transparent');
                    ctx.beginPath(); ctx.arc(hold.x, hold.y, 14 + hold.charge * 12, 0, Math.PI * 2);
                    ctx.fillStyle = gCore; ctx.fill();
                }

                if (hold.releasing && hold.releaseLife > 0) {
                    const sc = hold.charge;
                    if (hold.releaseLife > 0.92) {
                        const BLAST_R = 140 + sc * 320;
                        const BLAST_F = 2.0 + sc * 9.0;
                        nodes.forEach(n => {
                            if (n.isHub) return;
                            const dx = n.x - hold.x, dy = n.y - hold.y;
                            const d = Math.hypot(dx, dy);
                            if (d < BLAST_R && d > 0.5) {
                                const str = (1 - d / BLAST_R) * BLAST_F * sc;
                                n.vx += (dx / d) * str;
                                n.vy += (dy / d) * str;
                                n.flashLife = Math.min(1.0, n.flashLife + str * 0.14);
                            }
                        });
                        // Dopo il blast, spawn di alcuni segnali ricochet
                        if (signalsRef.current.length < MAX_SIGNALS - 3) {
                            for (let k = 0; k < 3; k++) {
                                const ri = Math.floor(Math.random() * nodes.length);
                                const sig = spawnSignal(nodes, ri, mainGrid, mainCols);
                                if (sig) signalsRef.current.push(sig);
                            }
                            lastSignalSpawnRef.current = t;
                        }
                    }
                    const BLAST_R = 140 + hold.charge * 320;
                    const elapsed = 1.0 - hold.releaseLife;
                    const shockR = elapsed * BLAST_R;
                    const shockA = (hold.releaseLife * hold.releaseLife * 0.85).toFixed(3);
                    ctx.beginPath(); ctx.arc(hold.x, hold.y, shockR, 0, Math.PI * 2);
                    ctx.strokeStyle = `${cursorColor2}${shockA})`;
                    ctx.lineWidth = 2.0 + hold.releaseLife * 2.5; ctx.stroke();
                    const shockR2 = Math.max(0, shockR - 40);
                    if (shockR2 > 0) {
                        ctx.beginPath(); ctx.arc(hold.x, hold.y, shockR2, 0, Math.PI * 2);
                        ctx.strokeStyle = `${cursorColor0}${(hold.releaseLife * 0.35).toFixed(3)})`;
                        ctx.lineWidth = 1.0; ctx.stroke();
                    }
                    hold.releaseLife -= 0.032;
                    if (hold.releaseLife <= 0) { hold.releasing = false; hold.charge = 0; }
                }
            }

            // ── 6. Movement + wrap + min-speed kick ───────────────────────────
            nodes.forEach(n => {
                if (!n.isHub) {
                    if (interactive && !hold.isDown && mouse.x > 0) {
                        const cd = Math.hypot(mouse.x - n.x, mouse.y - n.y);
                        if (cd < 85) {
                            const f = (85 - cd) / 85 * 0.004;
                            n.vx += (mouse.x - n.x) * f;
                            n.vy += (mouse.y - n.y) * f;
                        }
                    }
                    n.vx *= DAMPING; n.vy *= DAMPING;
                    const sp = Math.hypot(n.vx, n.vy);
                    if (sp < MIN_SPEED) {
                        const kickAngle = Math.random() * Math.PI * 2;
                        n.vx += Math.cos(kickAngle) * MIN_SPEED;
                        n.vy += Math.sin(kickAngle) * MIN_SPEED;
                    } else if (sp > 4.8) {
                        n.vx *= 4.8 / sp; n.vy *= 4.8 / sp;
                    }
                    n.x += n.vx; n.y += n.vy;
                    if (n.x < 0) n.x += W; else if (n.x >= W) n.x -= W;
                    if (n.y < 0) n.y += H; else if (n.y >= H) n.y -= H;
                }
                n.flashLife *= 0.84;
            });

            // ── Correzione posizionale MIN_NODE_GAP ───────────────────────────
            // Ricostruisce la grid DOPO il movimento per avere posizioni aggiornate
            const { grid: sepGrid, gCols: sepCols } = buildGrid(nodes, MIN_NODE_GAP * 3, W);
            for (let iter = 0; iter < 4; iter++) {
                nodes.forEach((a, i) => {
                    if (a.isHub) return;
                    getNeighbors(sepGrid, sepCols, a, MIN_NODE_GAP * 3).forEach(j => {
                        if (j <= i) return;
                        const b = nodes[j];
                        if (b.isHub) return;
                        const dx = b.x - a.x, dy = b.y - a.y;
                        const d = Math.hypot(dx, dy);
                        if (d < MIN_NODE_GAP && d > 0.001) {
                            const push = (MIN_NODE_GAP - d) * 0.55;
                            const nx = dx / d, ny = dy / d;
                            a.x -= nx * push; a.y -= ny * push;
                            b.x += nx * push; b.y += ny * push;
                        }
                    });
                });
            }

            // ── 7. Segnali Ricochet — auto-spawn ─────────────────────────────
            if (!reducedMotion && t - lastSignalSpawnRef.current > SIGNAL_SPAWN_INTERVAL) {
                if (signalsRef.current.length < MAX_SIGNALS / 2) {
                    const fromIdx = Math.floor(Math.random() * nodes.length);
                    const sig = spawnSignal(nodes, fromIdx, mainGrid, mainCols);
                    if (sig) signalsRef.current.push(sig);
                }
                lastSignalSpawnRef.current = t;
            }

            // Aggiorna segnali: avanza progress e gestisce ricochet
            {
                const nextSignals = [];
                signalsRef.current.forEach(sig => {
                    sig.progress += SIGNAL_SPEED * (reducedMotion ? 0.5 : 1);
                    if (sig.progress < 1.0) {
                        nextSignals.push(sig);
                    } else {
                        // Il segnale è arrivato al nodo destinazione
                        const toNode = nodes[sig.toIdx];
                        const fromNode = nodes[sig.fromIdx];
                        if (toNode) { toNode.flashLife = Math.min(1.0, toNode.flashLife + 0.6); toNode.signalGlow = 1.0; }
                        if (fromNode) { fromNode.signalGlow = Math.max(fromNode.signalGlow || 0, 0.5); }


                        // Ricochet: genera figli se non ha raggiunto il limite di hop
                        if (sig.hops < MAX_SIGNAL_HOPS && nextSignals.length < MAX_SIGNALS) {
                            const child1 = spawnSignal(nodes, sig.toIdx, mainGrid, mainCols, sig.visitedSet, sig.hops + 1);
                            if (child1) {
                                nextSignals.push(child1);
                                // Raro split a 2 rami — solo se c'è capienza
                                if (Math.random() < 0.28 && nextSignals.length < MAX_SIGNALS) {
                                    const child2 = spawnSignal(nodes, sig.toIdx, mainGrid, mainCols, sig.visitedSet, sig.hops + 1);
                                    if (child2 && child2.toIdx !== child1.toIdx) nextSignals.push(child2);
                                }
                            }
                        }
                    }
                });
                signalsRef.current = nextSignals;
            }

            // Bridge flash tra hub macro
            if (t - lastBridgeRef.current > 0.40) {
                const macroOn = hubs.filter(h => h.def.tier === 'macro' && h.influence > 0.4);
                for (let i = 0; i < macroOn.length; i++) {
                    for (let j = i + 1; j < macroOn.length; j++) {
                        if (
                            Math.hypot(macroOn[i].node.x - macroOn[j].node.x, macroOn[i].node.y - macroOn[j].node.y) < BRIDGE_DIST &&
                            Math.random() < 0.30
                        ) {
                            bridgesRef.current.push({ a: macroOn[i].node, b: macroOn[j].node, life: 1.0, decay: 0.014 });
                        }
                    }
                }
                lastBridgeRef.current = t;
            }

            // ── 8. Edge drawing (static network) ─────────────────────────────
            const drawnEdges = new Set();
            nodes.forEach((a, i) => {
                getNeighbors(mainGrid, mainCols, a, MAX_DIST).forEach(j => {
                    if (j <= i) return;
                    const edgeKey = i * nodes.length + j;
                    if (drawnEdges.has(edgeKey)) return;
                    drawnEdges.add(edgeKey);
                    const b = nodes[j];
                    const dist = Math.hypot(a.x - b.x, a.y - b.y);
                    if (dist < MAX_DIST) {
                        const a0 = (1 - dist / MAX_DIST);
                        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
                        ctx.strokeStyle = `rgba(${edgeRgb},${(a0 * 0.62).toFixed(3)})`;
                        ctx.lineWidth = 0.6 + a0 * 0.8; ctx.stroke();
                    }
                });
            });

            // Bridges hub-hub
            bridgesRef.current.forEach(p => {
                ctx.beginPath(); ctx.moveTo(p.a.x, p.a.y); ctx.lineTo(p.b.x, p.b.y);
                ctx.strokeStyle = `${NODE_COLORS[1]}${(p.life * 0.15).toFixed(3)})`;
                ctx.lineWidth = 0.65; ctx.stroke();
            });
            bridgesRef.current.forEach(p => {
                ctx.beginPath(); ctx.moveTo(p.a.x, p.a.y); ctx.lineTo(p.b.x, p.b.y);
                ctx.strokeStyle = `rgba(255,255,255,${(p.life * p.life * 0.65).toFixed(3)})`;
                ctx.lineWidth = 1.1 + p.life; ctx.stroke();
            });
            bridgesRef.current.forEach(p => { p.life -= p.decay; });
            bridgesRef.current = bridgesRef.current.filter(p => p.life > 0);

            // ── 9. Disegno segnali Ricochet ───────────────────────────────────
            signalsRef.current.forEach(sig => {
                const a = nodes[sig.fromIdx], b = nodes[sig.toIdx];
                if (!a || !b) return;
                const dist = Math.hypot(a.x - b.x, a.y - b.y);
                if (dist >= MAX_DIST) return;

                const p = sig.progress;
                const cx = a.x + (b.x - a.x) * p;
                const cy = a.y + (b.y - a.y) * p;

                // Edge highlighted durante il transito — fade in/out ai bordi
                const edgeAlpha = Math.min(p * 4, 1) * Math.min((1 - p) * 4, 1) * 0.65;
                ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
                ctx.strokeStyle = `rgba(255,255,255,${edgeAlpha.toFixed(3)})`;
                ctx.lineWidth = 0.9 + (1 - p) * 0.4; ctx.stroke();

            });


            // ── 10. Draw nodes — glow + hub glow + trail velocità ─────────────
            nodes.forEach(n => {
                n.pulse += reducedMotion ? 0.008 : 0.018;
                const pulse = Math.sin(n.pulse) * 0.5 + 0.5;
                const alpha = 0.58 + pulse * 0.42;
                const r = n.r + pulse * 0.55;


                // Flash halo (on receive)
                if (n.flashLife > 0.02) {
                    const fl = n.flashLife * n.flashLife;
                    const gr = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r * 4.5);
                    gr.addColorStop(0, `rgba(255,255,255,${(fl * 0.55).toFixed(3)})`);
                    gr.addColorStop(1, 'transparent');
                    ctx.beginPath(); ctx.arc(n.x, n.y, r * 4.5, 0, Math.PI * 2);
                    ctx.fillStyle = gr; ctx.fill();
                }
                // Signal glow: alone colorato su nodi che trasmettono
                n.signalGlow = (n.signalGlow || 0) * 0.88;
                if (n.signalGlow > 0.03) {
                    const sg = n.signalGlow;
                    const glowR = r * 6 * sg;
                    const grS = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, glowR);
                    grS.addColorStop(0, `${n.colorBase}${(sg * 0.70).toFixed(3)})`);
                    grS.addColorStop(0.4, `${n.colorBase}${(sg * 0.30).toFixed(3)})`);
                    grS.addColorStop(1, 'transparent');
                    ctx.beginPath(); ctx.arc(n.x, n.y, glowR, 0, Math.PI * 2);
                    ctx.fillStyle = grS; ctx.fill();
                }

                // Hub glow per tier
                if (n.isHub && !reducedMotion) {
                    const hubDef = hubs.find(h => h.node === n)?.def;
                    if (hubDef) {
                        const glowMult = HUB_GLOW[hubDef.tier] || 4;
                        const glowR = r * glowMult * (1 + pulse * 0.3);
                        const glowA = hubDef.tier === 'macro' ? 0.18 : hubDef.tier === 'meso' ? 0.12 : 0.08;
                        const gHub = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, glowR);
                        gHub.addColorStop(0, `${n.colorBase}${(glowA + pulse * 0.06).toFixed(3)})`);
                        gHub.addColorStop(1, 'transparent');
                        ctx.beginPath(); ctx.arc(n.x, n.y, glowR, 0, Math.PI * 2);
                        ctx.fillStyle = gHub; ctx.fill();
                    }
                }

                // Depth scaling: nodi 3D in primo piano sono più grandi/luminosi
                const depthScale = 1 + (n.zDepth || 0) * 0.40;
                const rD = r * Math.max(0.4, depthScale);
                const alphaD = Math.min(0.98, alpha * Math.max(0.45, 0.75 + (n.zDepth || 0) * 0.25));

                if (!reducedMotion) {
                    ctx.save();
                    ctx.shadowColor = n.colorBase + '0.8)';
                    ctx.shadowBlur = (6 + pulse * 4) * Math.max(0.5, depthScale);
                    ctx.beginPath(); ctx.arc(n.x, n.y, rD, 0, Math.PI * 2);
                    const wt = Math.min(1, n.flashLife * 0.55);
                    ctx.fillStyle = wt > 0.05
                        ? `rgba(${Math.round(167 * (1 - wt) + 255 * wt)},${Math.round(139 * (1 - wt) + 255 * wt)},${Math.round(250 * (1 - wt) + 255 * wt)},${alphaD.toFixed(2)})`
                        : `${n.colorBase}${alphaD.toFixed(2)})`;
                    ctx.fill();
                    ctx.restore();
                } else {
                    ctx.beginPath(); ctx.arc(n.x, n.y, rD, 0, Math.PI * 2);
                    ctx.fillStyle = `${n.colorBase}${alphaD.toFixed(2)})`;
                    ctx.fill();
                }
            });

            // ── 11. Cursor indicator ──────────────────────────────────────────
            if (interactive) drawCursor(nodes, mouse);

            animRef.current = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            cancelAnimationFrame(animRef.current);
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseleave', onMouseLeave);
            window.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mouseup', onMouseUp);
            canvas.removeEventListener('touchmove', onTouchMove);
            canvas.removeEventListener('touchstart', onTouchStart);
            canvas.removeEventListener('touchend', onTouchEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colorScheme, interactive, nodeCount, reducedMotion]);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 w-full h-full"
            style={{ background: '#030014', touchAction: 'none' }}
        />
    );
};

export default NeuralCanvasV2;
